"use client";

import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import app from "../../../../global-account/app.js";
import { Button, IPFSMediaRenderer, toast, useAuthentication, useB3Config, useProfile } from "../../../../global-account/react/index.js";
import { validateImageUrl } from "../../../../global-account/react/utils/profileDisplay.js";
import { cn } from "../../../../shared/utils/cn.js";
import { debugB3React } from "../../../../shared/utils/debug.js";
import { getIpfsUrl } from "../../../../shared/utils/ipfs.js";
import { uploadFile as uploadToB3 } from "../../../../shared/utils/upload.js";
import { Loader2, Upload, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { useActiveAccount } from "thirdweb/react";
import { useModalStore } from "../../stores/index.js";
import ModalHeader from "../ModalHeader/ModalHeader.js";
const debug = debugB3React("AvatarEditor");
// Helper function to create an image element from a URL
const createImage = url => new Promise((resolve, reject) => {
  const image = new Image();
  image.addEventListener("load", () => resolve(image));
  image.addEventListener("error", error => reject(error));
  image.setAttribute("crossOrigin", "anonymous");
  image.src = url;
});
export function AvatarEditor({
  onSetAvatar,
  className
}) {
  const [viewStep, setViewStep] = useState("select");
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [selectedProfileType, setSelectedProfileType] = useState(null); // Track which profile was selected
  const [hoveredProfile, setHoveredProfile] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [crop, setCrop] = useState({
    x: 0,
    y: 0
  });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileInputRef = useRef(null);
  const {
    partnerId
  } = useB3Config();
  const {
    user,
    setUser
  } = useAuthentication(partnerId);
  const setB3ModalContentType = useModalStore(state => state.setB3ModalContentType);
  const contentType = useModalStore(state_0 => state_0.contentType);
  const {
    authStrategy
  } = useB3Config();
  const isBetterAuth = authStrategy === "better-auth";
  const account = useActiveAccount();
  const {
    data: profile,
    refetch: refreshProfile
  } = useProfile({
    address: account?.address,
    fresh: true
  });
  // Get raw avatar URLs, convert IPFS URLs, and validate them
  const rawCurrentAvatar = user?.avatar || profile?.avatar;
  const currentAvatar = validateImageUrl(rawCurrentAvatar);
  const safePreviewUrl = validateImageUrl(previewUrl);
  const onCropComplete = useCallback((_croppedArea, croppedAreaPixels_0) => {
    setCroppedAreaPixels(croppedAreaPixels_0);
  }, []);
  const createCroppedImage = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Failed to get canvas context");
    }
    // Set canvas size to the crop area
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    // Draw the cropped image
    ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
    // Return as blob
    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        resolve(blob);
      }, "image/jpeg");
    });
  };
  const handleFileSelect = event => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      setSelectedFile(file);
      // Clear profile type selection when uploading a new file
      setSelectedProfileType(null);
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setSelectedAvatar(url);
    }
  };
  const handleRemovePreview = () => {
    setSelectedAvatar(currentAvatar || null);
    setSelectedProfileType(null);
    setSelectedFile(null);
    if (previewUrl) {
      // Only revoke blob URLs (from file uploads), not regular URLs (from social profiles)
      if (previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    // Reset crop state
    setCrop({
      x: 0,
      y: 0
    });
    setZoom(1);
    setCroppedAreaPixels(null);
  };
  const handleSaveChanges = async () => {
    if (!isBetterAuth && !account?.address) {
      toast.error("No account connected");
      return;
    }
    setIsSaving(true);
    try {
      let fileToUpload = null;
      // If user uploaded a new file and cropped it
      if (selectedFile && previewUrl && croppedAreaPixels) {
        try {
          const croppedBlob = await createCroppedImage(previewUrl, croppedAreaPixels);
          const extension = selectedFile.name.split(".").pop() || "jpg";
          fileToUpload = new File([croppedBlob], `avatar-cropped.${extension}`, {
            type: "image/jpeg"
          });
        } catch (error_0) {
          debug("Error cropping image:", error_0);
          toast.error("Failed to crop image. Please try again.");
          setIsSaving(false);
          return;
        }
      } else if (selectedFile) {
        // Fallback if no crop was made
        fileToUpload = selectedFile;
      } else if (selectedProfileType && previewUrl && croppedAreaPixels) {
        // User selected from social profile and cropped it
        debug("Cropping social profile image:", previewUrl);
        try {
          const croppedBlob_0 = await createCroppedImage(previewUrl, croppedAreaPixels);
          fileToUpload = new File([croppedBlob_0], `avatar-${selectedProfileType}-cropped.jpg`, {
            type: "image/jpeg"
          });
          debug("Successfully cropped social profile image");
        } catch (error_1) {
          debug("Error cropping social profile image:", error_1);
          toast.error("Failed to crop image. Please try again.");
          setIsSaving(false);
          return;
        }
      } else if (selectedProfileType && selectedAvatar) {
        // User selected from existing profile avatars without cropping
        // Fetch the image from the URL and convert to blob
        debug("Fetching image from social profile:", selectedAvatar);
        try {
          // Convert IPFS URLs to gateway URLs for fetching
          const httpUrl = getIpfsUrl(selectedAvatar);
          const response = await fetch(httpUrl);
          if (!response.ok) {
            throw new Error("Failed to fetch image");
          }
          const blob_0 = await response.blob();
          debug("Fetched blob with type:", blob_0.type);
          // Determine the correct extension from the blob's MIME type
          // This handles URLs without extensions (like Farcaster images)
          const mimeToExtension = {
            "image/jpeg": "jpg",
            "image/jpg": "jpg",
            "image/png": "png",
            "image/gif": "gif",
            "image/webp": "webp",
            "image/svg+xml": "svg"
          };
          const extension_0 = blob_0.type ? mimeToExtension[blob_0.type.toLowerCase()] || "jpg" : "jpg";
          const mimeType = blob_0.type || `image/${extension_0}`;
          fileToUpload = new File([blob_0], `avatar-${selectedProfileType}.${extension_0}`, {
            type: mimeType
          });
          debug("Successfully converted social profile image to file with extension:", extension_0);
        } catch (fetchError) {
          debug("Error fetching social profile image:", fetchError);
          toast.error("Failed to fetch profile image. Please try uploading manually.");
          setIsSaving(false);
          return;
        }
      }
      // Upload file if we have one
      if (fileToUpload) {
        debug("Starting upload", fileToUpload);
        const avatarUrl = await uploadToB3(fileToUpload);
        debug("Upload successful", avatarUrl);
        // Save avatar URL using profiles service
        const user_0 = await app.service("users").setAvatar({
          avatar: avatarUrl
        },
        // @ts-expect-error - our typed client is expecting context even though it's set elsewhere
        {});
        // update user
        // @ts-expect-error this resolved fine, look into why expect-error needed
        setUser(user_0);
        toast.success("Looks great! Your avatar has been saved!");
      }
      // Refresh profile to get updated avatar
      await refreshProfile();
      onSetAvatar?.();
    } catch (error) {
      debug("Error saving avatar:", error);
      toast.error("Failed to save avatar. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };
  const handleCancel = () => {
    if (viewStep === "upload") {
      setViewStep("select");
      handleRemovePreview();
    } else {
      setB3ModalContentType({
        type: "manageAccount",
        chain: contentType?.chain,
        partnerId: partnerId
      });
    }
  };
  const handleProfileAvatarSelect = (avatarUrl_0, profileType) => {
    setSelectedAvatar(avatarUrl_0);
    setSelectedProfileType(profileType);
    // Convert IPFS URLs to gateway URLs so the cropper can load them
    const httpUrl_0 = getIpfsUrl(avatarUrl_0, 1);
    // Set preview URL to the avatar so it shows in the cropper
    setPreviewUrl(httpUrl_0);
    // Clear any selected file since we're selecting from profile
    setSelectedFile(null);
    // Move to upload/crop view
    setViewStep("upload");
  };
  const handleUploadImageClick = () => {
    setViewStep("upload");
  };
  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };
  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = e_0 => {
    e_0.preventDefault();
    e_0.stopPropagation();
    setIsDragging(false);
  };
  const handleDragOver = e_1 => {
    e_1.preventDefault();
    e_1.stopPropagation();
  };
  const handleDrop = e_2 => {
    e_2.preventDefault();
    e_2.stopPropagation();
    setIsDragging(false);
    const file_0 = e_2.dataTransfer.files?.[0];
    if (file_0) {
      // Validate file type
      if (!file_0.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      // Validate file size (max 5MB)
      if (file_0.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      setSelectedFile(file_0);
      // Clear profile type selection when uploading a new file
      setSelectedProfileType(null);
      // Create preview URL
      const url_0 = URL.createObjectURL(file_0);
      setPreviewUrl(url_0);
      setSelectedAvatar(url_0);
    }
  };
  const handleLinkMoreAccount = () => {
    setB3ModalContentType({
      type: "linkAccount",
      chain: contentType?.chain,
      partnerId: partnerId
    });
  };
  const isLoading = isSaving;
  // Get profile avatars with validated URLs
  const profileAvatars = profile?.profiles?.filter(p => p.avatar).map(p_0 => {
    const rawAvatarUrl = p_0?.avatar || "";
    const validatedUrl = validateImageUrl(rawAvatarUrl);
    return {
      type: p_0.type,
      avatar: validatedUrl,
      name: p_0.name || p_0.type
    };
  }).filter(p_1 => p_1.avatar !== null) || []; // Filter out profiles with invalid avatars
  return _jsxs("div", {
    className: cn("b3-modal-avatar-editor dark:bg-b3-background flex w-full max-w-md flex-col bg-white", className),
    children: [viewStep === "upload" && _jsx(ModalHeader, {
      title: "Upload Image"
    }), _jsxs("div", {
      className: "flex flex-col items-center p-6",
      children: [viewStep === "select" ? _jsxs(_Fragment, {
        children: [_jsx("div", {
          className: "relative mb-6",
          children: _jsx("div", {
            className: "h-32 w-32 overflow-hidden rounded-full",
            children: safePreviewUrl || selectedAvatar || currentAvatar ? _jsx(IPFSMediaRenderer, {
              src: safePreviewUrl || selectedAvatar || currentAvatar || "",
              alt: "Avatar preview",
              className: "h-full w-full object-cover"
            }) : _jsx("div", {
              className: "bg-b3-primary-wash h-full w-full"
            })
          })
        }), _jsxs("button", {
          onClick: handleUploadImageClick,
          className: "font-inter dark:border-b3-line dark:bg-b3-react-primary mb-6 flex w-full items-center justify-center gap-2 rounded-xl border border-[#e4e4e7] bg-white px-4 py-3 text-sm font-semibold text-[#18181b] shadow-sm transition-colors hover:bg-[#f4f4f5] dark:text-white",
          children: [_jsx(Upload, {
            className: "h-4 w-4"
          }), "Upload image"]
        }), _jsxs("div", {
          className: "w-full",
          children: [_jsx("h3", {
            className: "mb-2 text-base font-semibold text-[#18181b]",
            children: "Select your profile image"
          }), _jsx("p", {
            className: "mb-4 text-sm font-semibold text-[#475467]",
            children: "Pick an avatar from your linked profiles, ENS or upload a new one."
          }), _jsx("div", {
            className: "mb-4 flex gap-3",
            children: profileAvatars.map((profileAvatar, index) => {
              // Skip if avatar is null (should not happen due to filter, but TypeScript doesn't know that)
              if (!profileAvatar.avatar) return null;
              return _jsxs("div", {
                className: "relative",
                onMouseEnter: () => setHoveredProfile(profileAvatar.type),
                onMouseLeave: () => setHoveredProfile(null),
                children: [_jsx("button", {
                  onClick: () => handleProfileAvatarSelect(profileAvatar.avatar || "", profileAvatar.type || ""),
                  className: cn("h-16 w-16 overflow-hidden rounded-full border-2 transition-all", selectedProfileType === profileAvatar.type ? "border-[#3368ef] ring-2 ring-[#3368ef]/20" : "border-transparent hover:border-[#e4e4e7]"),
                  children: _jsx(IPFSMediaRenderer, {
                    src: profileAvatar.avatar || "",
                    alt: `${profileAvatar.type} avatar`,
                    className: "h-full w-full object-cover"
                  })
                }), hoveredProfile === profileAvatar.type && _jsx("div", {
                  className: "absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-[#18181b] px-3 py-1.5 text-xs text-white",
                  children: profileAvatar.name
                })]
              }, profileAvatar.type + "-" + index);
            })
          }), _jsxs("button", {
            onClick: handleLinkMoreAccount,
            className: "b3-modal-link-more-account font-inter flex items-center gap-2 text-sm font-semibold text-[#3368ef] hover:underline",
            children: [_jsx("svg", {
              width: "16",
              height: "16",
              viewBox: "0 0 16 16",
              fill: "none",
              xmlns: "http://www.w3.org/2000/svg",
              className: "h-4 w-4",
              children: _jsx("path", {
                d: "M8.75 2.75C8.75 2.33579 8.41421 2 8 2C7.58579 2 7.25 2.33579 7.25 2.75V7.25H2.75C2.33579 7.25 2 7.58579 2 8C2 8.41421 2.33579 8.75 2.75 8.75H7.25V13.25C7.25 13.6642 7.58579 14 8 14C8.41421 14 8.75 13.6642 8.75 13.25V8.75H13.25C13.6642 8.75 14 8.41421 14 8C14 7.58579 13.6642 7.25 13.25 7.25H8.75V2.75Z",
                fill: "currentColor"
              })
            }), "Link more accounts"]
          })]
        })]
      }) : _jsx(_Fragment, {
        children: !selectedFile && !previewUrl ? _jsxs("div", {
          onClick: handleOpenFilePicker,
          onDragEnter: handleDragEnter,
          onDragLeave: handleDragLeave,
          onDragOver: handleDragOver,
          onDrop: handleDrop,
          className: cn("b3-modal-upload-view mb-6 flex w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-16 transition-colors", isDragging ? "dark:border-b3-line dark:bg-b3-react-primary border-[#3368ef] bg-[#f0f5ff]" : "dark:border-b3-line dark:bg-b3-background border-[#e4e4e7] hover:border-[#3368ef] hover:bg-[#f0f5ff]"),
          children: [_jsxs("p", {
            className: "font-inter mb-1 text-sm",
            children: [_jsx("span", {
              className: "font-semibold text-[#3368ef] dark:text-white",
              children: "Click to upload"
            }), _jsx("span", {
              className: "text-[#71717a]",
              children: " or drag and drop"
            })]
          }), _jsx("p", {
            className: "text-xs text-[#71717a] dark:text-white",
            children: "PNG, JPG or GIF (up to 5MB)"
          })]
        }) : _jsxs("div", {
          className: "mb-6 w-full",
          children: [_jsx("div", {
            className: "relative aspect-square w-full overflow-hidden rounded-xl bg-[#f4f4f5]",
            children: safePreviewUrl ? _jsxs(_Fragment, {
              children: [_jsx(Cropper, {
                image: safePreviewUrl,
                crop: crop,
                zoom: zoom,
                aspect: 1,
                onCropChange: setCrop,
                onCropComplete: onCropComplete,
                onZoomChange: setZoom,
                cropShape: "rect",
                showGrid: false,
                style: {
                  containerStyle: {
                    width: "100%",
                    height: "100%",
                    backgroundColor: "#f4f4f5"
                  },
                  cropAreaStyle: {
                    border: "2px solid #3368ef",
                    borderRadius: "0px"
                  }
                }
              }), _jsx("button", {
                onClick: handleRemovePreview,
                className: "absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-[#51525c] text-white transition-colors hover:bg-[#71717a]",
                children: _jsx(X, {
                  className: "h-4 w-4"
                })
              })]
            }) : _jsx("div", {
              className: "bg-b3-primary-wash h-full w-full"
            })
          }), safePreviewUrl && _jsxs("div", {
            className: "mt-4 flex items-center gap-3",
            children: [_jsx("label", {
              className: "shrink-0 text-sm font-semibold text-[#475467]",
              children: "Zoom"
            }), _jsx("input", {
              type: "range",
              min: 1,
              max: 3,
              step: 0.1,
              value: zoom,
              onChange: e_3 => setZoom(Number(e_3.target.value)),
              className: "flex-1 accent-[#3368ef]"
            })]
          })]
        })
      }), _jsx("input", {
        ref: fileInputRef,
        type: "file",
        accept: "image/*",
        onChange: handleFileSelect,
        className: "hidden"
      })]
    }), _jsxs("div", {
      className: "font-inter flex gap-3 border-t border-[#e4e4e7] p-6 font-semibold",
      children: [_jsx(Button, {
        onClick: handleCancel,
        variant: "outline",
        disabled: isLoading,
        className: "dark:border-b3-line dark:bg-b3-background flex-1 rounded-xl border-[#e4e4e7] text-[#18181b] hover:bg-[#f4f4f5] dark:text-white",
        children: "Cancel"
      }), _jsx(Button, {
        onClick: handleSaveChanges,
        disabled: isLoading || !selectedFile && !selectedProfileType,
        className: "b3-modal-save-button flex-1 rounded-xl bg-[#3368ef] text-white hover:bg-[#2952cc]",
        children: isLoading ? _jsxs(_Fragment, {
          children: [_jsx(Loader2, {
            className: "mr-2 h-4 w-4 animate-spin"
          }), "Saving..."]
        }) : "Save changes"
      })]
    })]
  });
}